import SelectInput from "../../../../components/inputs/SelectInput";
import { InlineMath, BlockMath } from "react-katex";
import { getRandomInt } from "../../../../utils/randomInt";

export const id = "ClassifyCircleFromGeneralTask";
export const displayName = "Klasyfikuj: okrąg, punkt, czy nie istnieje";

const options = [
  { value: "okrąg", label: "okrąg" },
  { value: "punkt", label: "punkt" },
  { value: "nie istnieje", label: "nie istnieje" }
];

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}


export function generateTask() {
  let Dx = getRandomInt(-8, 8);
  while (Dx % 2 !== 0) Dx = getRandomInt(-8, 8);

  let Ey = getRandomInt(-8, 8);
  while (Ey % 2 !== 0) Ey = getRandomInt(-8, 8);

  const a = Dx / 2;
  const b = Ey / 2;
  const shiftX = a ** 2;
  const shiftY = b ** 2;

  const rSquared = getRandomInt(-5, 25);
  const F = shiftX + shiftY - rSquared;

  let classification = "";
  if (rSquared > 0) classification = "okrąg";
  else if (rSquared === 0) classification = "punkt";
  else classification = "nie istnieje";

  return {
    type: "ClassifyCircleFromGeneralTask",
    data: {
      Dx, Ey, F, a, b, rSquared, classification
    }
  };
}


export function renderTask(task) {
  const { Dx, Ey, F } = task.data;

  const parts = [
    "x^2 + y^2",
    Dx !== 0 ? (Dx > 0 ? `+ ${Dx}x` : `- ${Math.abs(Dx)}x`) : null,
    Ey !== 0 ? (Ey > 0 ? `+ ${Ey}y` : `- ${Math.abs(Ey)}y`) : null,
    F !== 0 ? (F > 0 ? `+ ${F}` : `- ${Math.abs(F)}`) : null
  ].filter(Boolean);

  return (
    <div className="space-y-2">
      <p>Czy istnieje okrąg opisany równaniem:</p>
      <BlockMath math={`${parts.join(" ")} = 0`} />
      <p>Wybierz poprawną odpowiedź:</p>
    </div>
  );
}


export function renderInput(_, userInput, setUserInput, disabled) {
  return (
    <SelectInput
      options={options}
      value={userInput}
      onChange={(e) => setUserInput(e?.target?.value ?? e)} // obsługa obu formatów
      disabled={disabled}
      placeholder="Wybierz"
    />
  );
}

export function checkAnswer(task, userInput) {
  return userInput === task.data.classification;
}

export function renderAnswer(task) {
  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      Poprawna odpowiedź: <strong>{task.data.classification}</strong>
    </div>
  );
}


export function renderExplanation(input) {
  const data = input?.data || input;

  if (!data || data.Dx === undefined || data.Ey === undefined || data.F === undefined) {
    console.warn("⚠️ Niepoprawne dane w renderExplanation:", data);
    return (
      <div style={{ color: "red" }}>
        Błąd: brak lub niepełne dane do wyjaśnienia
      </div>
    );
  }

  const { Dx, Ey, F, classification } = data;

  const a = -Dx / 2;
  const b = -Ey / 2;
  const aLatex = formatMixedFractionLatex(a);
  const bLatex = formatMixedFractionLatex(b);
  const a2 = formatMixedFractionLatex(a ** 2);
  const b2 = formatMixedFractionLatex(b ** 2);
  const F_latex = formatMixedFractionLatex(F);
  const rSquared = a ** 2 + b ** 2 - F;
  const r2Latex = formatMixedFractionLatex(rSquared);

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Porównujemy równanie ogólne z postacią:
        <BlockMath math={`x^2 + y^2 ${Dx >= 0 ? "+ " + Dx : "- " + Math.abs(Dx)}x ${Ey >= 0 ? "+ " + Ey : "- " + Math.abs(Ey)}y ${F >= 0 ? "+ " + F : "- " + Math.abs(F)} = 0`} />
        <BlockMath math={`x^2 + y^2 - 2ax - 2by + c = 0`} />
        <p>Odczytujemy:</p>
        <ul className="list-disc ml-6">
          <li><BlockMath math={`a = -\\frac{${Dx}}{2} = ${aLatex}`} /></li>
          <li><BlockMath math={`b = -\\frac{${Ey}}{2} = ${bLatex}`} /></li>
          <li><BlockMath math={`c = ${F_latex}`} /></li>
        </ul>
      </div>

      <div>
        <strong>Krok 2:</strong> Obliczamy <InlineMath math="r^2" />:
        <BlockMath math={`r^2 = a^2 + b^2 - c = ${a2} + ${b2} - (${F_latex}) = ${r2Latex}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Ponieważ <InlineMath math={`r^2 = ${r2Latex}`} />, równanie opisuje:
        <div className="mt-2">
          <strong>{classification}</strong>
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput) {
  if (!userInput) return <i>(brak odpowiedzi)</i>;
  return <span>{userInput}</span>;
}
